"use client";

import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Target, Loader2, Check, Clock } from "lucide-react";
import { useSettings } from "@/hooks/use-settings";
import { centsToInput, parseCurrency } from "@/lib/format";
import { toast } from "@/hooks/use-toast";
import { getErrorMessage } from "@/lib/errors";
import type { Settings } from "@convex/settings";

interface GoalsSectionProps {
  settings: Settings;
}

export function GoalsSection({ settings }: GoalsSectionProps) {
  const { updateSettings } = useSettings();
  const [isSaving, setIsSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // Form state
  const [weeklyGoal, setWeeklyGoal] = useState(
    settings.weeklyGoal > 0 ? centsToInput(settings.weeklyGoal) : ""
  );
  const [weeklyHourLimit, setWeeklyHourLimit] = useState(
    settings.weeklyHourLimit.toString()
  );
  const [dailyHourLimit, setDailyHourLimit] = useState(
    settings.dailyHourLimit.toString()
  );
  const [extendedHours, setExtendedHours] = useState(settings.extendedHours);

  // Track if values changed
  const hasChanges =
    parseCurrency(weeklyGoal) !== settings.weeklyGoal ||
    parseFloat(weeklyHourLimit) !== settings.weeklyHourLimit ||
    parseFloat(dailyHourLimit) !== settings.dailyHourLimit ||
    extendedHours !== settings.extendedHours;

  // Reset saved state when changes are made
  useEffect(() => {
    if (hasChanges) setSaved(false);
  }, [hasChanges]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await updateSettings({
        weeklyGoal: parseCurrency(weeklyGoal),
        weeklyHourLimit: parseFloat(weeklyHourLimit) || (extendedHours ? 50 : 40),
        dailyHourLimit: parseFloat(dailyHourLimit) || (extendedHours ? 10 : 8),
        extendedHours,
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (error) {
      toast({
        title: "Failed to save goals",
        description: getErrorMessage(error),
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Target className="h-5 w-5 text-muted-foreground" />
          <CardTitle className="text-lg">Goals</CardTitle>
        </div>
        <CardDescription>Set your earnings and hour targets</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Extended Hours Toggle */}
        <div className="flex items-center justify-between p-3 border rounded-lg">
          <div className="space-y-0.5">
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <Label htmlFor="extendedHours" className="font-medium">
                Extended Hours
              </Label>
            </div>
            <p className="text-xs text-muted-foreground">
              Market-exclusive: 50hr weekly / 10hr daily limits
            </p>
          </div>
          <Switch
            id="extendedHours"
            checked={extendedHours}
            onCheckedChange={(checked) => {
              setExtendedHours(checked);
              // Auto-update limits when toggling
              if (checked) {
                setWeeklyHourLimit("50");
                setDailyHourLimit("10");
              } else {
                setWeeklyHourLimit("40");
                setDailyHourLimit("8");
              }
            }}
          />
        </div>

        {/* Weekly Earnings Goal */}
        <div className="space-y-2">
          <Label htmlFor="weeklyGoal">Weekly Earnings Goal</Label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
              $
            </span>
            <Input
              id="weeklyGoal"
              type="number"
              inputMode="decimal"
              step="0.01"
              min="0"
              placeholder="0.00"
              value={weeklyGoal}
              onChange={(e) => setWeeklyGoal(e.target.value)}
              className="pl-7"
            />
          </div>
          <p className="text-xs text-muted-foreground">
            Leave empty for no goal. Progress shown on dashboard.
          </p>
        </div>

        {/* Weekly Hour Limit */}
        <div className="space-y-2">
          <Label htmlFor="weeklyHourLimit">Weekly Hour Limit</Label>
          <div className="relative">
            <Input
              id="weeklyHourLimit"
              type="number"
              inputMode="numeric"
              min="1"
              max="168"
              value={weeklyHourLimit}
              onChange={(e) => setWeeklyHourLimit(e.target.value)}
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">
              hours
            </span>
          </div>
          <p className="text-xs text-muted-foreground">
            Amazon Flex rolling 7-day limit (default: {extendedHours ? "50" : "40"} hours)
          </p>
        </div>

        {/* Daily Hour Limit */}
        <div className="space-y-2">
          <Label htmlFor="dailyHourLimit">Daily Hour Limit</Label>
          <div className="relative">
            <Input
              id="dailyHourLimit"
              type="number"
              inputMode="numeric"
              min="1"
              max="24"
              value={dailyHourLimit}
              onChange={(e) => setDailyHourLimit(e.target.value)}
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">
              hours
            </span>
          </div>
          <p className="text-xs text-muted-foreground">
            Maximum hours per day (default: {extendedHours ? "10" : "8"} hours)
          </p>
        </div>

        {/* Save Button */}
        <Button
          type="button"
          onClick={handleSave}
          disabled={!hasChanges || isSaving}
          className="w-full"
        >
          {isSaving ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : saved ? (
            <>
              <Check className="h-4 w-4 mr-2" />
              Saved
            </>
          ) : (
            "Save Goals"
          )}
        </Button>
      </CardContent>
    </Card>
  );
}
